import { useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { Doughnut } from "react-chartjs-2";
import { themeQuartz } from "ag-grid-community";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";

import getNameFromEmail from '/src/utils/getNameFromEmail';
import "./AgentsPerformanceArray.css";
import ProgressBarCircular from "../../../conversations/components/ProgressBarCircular/ProgressBarCircular";
import ModalAgentPerformance from "../ModalAgentPerformance/ModalAgentPerformance";
import NiceModal from "@ebay/nice-modal-react";

const CONVERSATIONS_LIST_PATH = '/supervision/conversations/list';

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
const displayNameFromEmail = (email) => {
    const { firstName, lastName } = getNameFromEmail(email);
    return `${capitalize(firstName)} ${capitalize(lastName)}`.trim() || email;
};

function AgentsPerformanceArray({ agents = [], isLoading = false }) {
    const toast = useToastDispatchContext();

    const copyEmail = (email) => (e) => {
        e.stopPropagation();
        if (!email) return;
        navigator.clipboard.writeText(email).then(() => {
            toast.success("Copy to clipboard", "You can paste this email where you need !", 5000);
        });
    };

    const rowData = useMemo(() => agents.map((a) => ({
        name: a.agent,
        initials: (a.agent || "").slice(0, 2).toUpperCase(),
        pole: a.pole || "",
        avgScore: a.avgScore,
        interactions: a.interactions,
        training: a.training ?? 0,
        speakerRatio: a.speakerRatio || { agent: 0, prospect: 0 },
        failing: a.topFailingCriterion || "N/A",
        internal: a.internal,
        regulatory: a.regulatory,
        raw: a,
    })), [agents]);

    const buildInteractionsHref = (agent) =>
        `${CONVERSATIONS_LIST_PATH}?agent_id=${encodeURIComponent(agent)}`;

    const columnDefs = useMemo(() => [
        {
            headerName: "Name",
            field: "name",
            flex: 1,
            cellRenderer: (params) => (
                <div className="user-cell">
                    <Tippy
                        content={
                            <div className="reviewers-bubbles_tooltip">
                                <div className="reviewers-bubbles_tooltip-name">{params.data.name}</div>
                                <div className="reviewers-bubbles_tooltip-hint">Click to copy email</div>
                            </div>
                        }
                        placement="top"
                        delay={[150, 0]}
                    >
                        <span className="no-row-click" style={{ color: "var(--main)", cursor: "pointer" }} onClick={copyEmail(params.data.name)}>{displayNameFromEmail(params.data.name)}</span>
                    </Tippy>
                </div>
            )
        },
        { headerName: "Pole", field: "pole" },

        {
            headerName: "Avg score",
            sort: "asc",
            field: "avgScore",
            cellRenderer: (p) => `${p.value}%`
        },

        {
            headerName: "Interactions",
            field: "interactions",
            cellRenderer: (params) => (
                <a
                    className="no-row-click"
                    href={buildInteractionsHref(params.data.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: "var(--main)", fontWeight: 600 }}
                >
                    {params.value}
                </a>
            )
        },
        { headerName: "Training assigned", field: "training" },

        {
            headerName: "Speaker ratio",
            field: "speakerRatio",
            cellRenderer: (params) => (
                <div className="center-cell">
                    <ProgressBarCircular value={params.data.speakerRatio.agent} size={50} />
                </div>
            )
        },

        { headerName: "Top failing criterion", field: "failing" },

        {
            headerName: "Internal",
            field: "internal",
            cellRenderer: (p) => p.value != null ? `${p.value}%` : "N/A"
        },

        {
            headerName: "Regulatory",
            field: "regulatory",
            cellRenderer: (p) => p.value != null ? `${p.value}%` : "N/A"
        }
    ], []);

    const defaultColDef = {
        sortable: true,
        resizable: true,
        filter: true,
        flex: 1,
        cellStyle: {
            display: "flex",
            alignItems: "center"
        }
    };

    const onRowClicked = (event) => {
        if (event.event?.target?.closest?.('.no-row-click')) return;
        NiceModal.show(ModalAgentPerformance, {
            item: event.data.raw
        });
    };


    return (
        <div className="ag-theme-alpine container">
            <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowHeight={70}
                pagination={true}
                paginationPageSize={10}
                theme={themeQuartz}
                onRowClicked={onRowClicked}
            />
        </div>
    );
}

export default AgentsPerformanceArray;